import { ModalNL } from "@components/modal/Modal"
import * as Accordion from "@radix-ui/react-accordion"
import * as DropdownMenu from "@radix-ui/react-dropdown-menu"
import { useAppSelector } from "@redux/app/hooks"
import { getCurrentCompany } from "@redux/features/currentCompany/currentCompanySlice"
import { getUserData } from "@redux/features/userData/userDataSlice"
import { useQueryClient } from "@tanstack/react-query"
import { Button, IconButton, LoadingCircle } from "@ui-lib/index"
import { requestApi2 } from "@utils/http"
import { portalSocket } from "@utils/websocket/portalSocket"
import { seempSocket } from "@utils/websocket/seempSocket"
import { seempSocket2 } from "@utils/websocket/seempSocket2"
import { endpoints, SeempAction, SeempActionGroup, SeempActionResult, SeempReport } from "api"
import { t } from "i18next"
import { EventSourceManager } from "open-lib"
import { useEffect, useMemo, useRef, useState } from "react"
import { MdDelete, MdInfoOutline, MdSettings, MdSort } from "react-icons/md"
import { useSeempGetUsersQuery } from "./queries/useSeempGetUsersQuery"
import SeempActionCard from "./SeempActionCard"
import SeempActionGroupComponent from "./SeempActionGroup"
import { getHandlePlantCacheUpdated } from "./SeempContent"
import SeempDeleteReportBody from "./SeempDeleteReportBody"
import SeempInfoCard from "./SeempInfoCard"
import SeempNewActionCard from "./SeempNewActionCard"
import { CheckIcon } from "./SeempNewReportBody"
import SeempUpdateReportBody from "./SeempUpdateReportBody"

export type LockedActionList = {
	reportId: number
	actions: { actionId: number; userEmail: string; userName: string }[]
}[]
export default function SeempReportBody({
	plantId,
	reportData,
	actionGroups,
	onRefetchReports,
}: {
	plantId: number
	reportData: SeempReport
	actionGroups: SeempActionGroup[]
	onRefetchReports?: () => ReturnType<typeof Promise.resolve>
}) {
	const queryClient = useQueryClient()
	const handlePlantCacheUpdated = getHandlePlantCacheUpdated(queryClient)
	const userData = useAppSelector(getUserData)
	const companyName = useAppSelector(getCurrentCompany)
	const [lockedActionList, setLockedActionList] = useState<LockedActionList>([])

	const [reportErrors, setReportErrors] = useState<{ actionId: number; nErrors: number }[]>(
		reportData.actions.map((action) => {
			return { actionId: action.id, nErrors: 0 }
		})
	)

	const totalErrors = reportErrors.reduce((acc, item) => acc + item.nErrors, 0)

	const handleRefetchReport = async () => {
		onRefetchReports?.()
	}

	const {
		isLoading: isUsersListLoading,
		isError: isUsersListError,
		data: usersList,
	} = useSeempGetUsersQuery({ companyName })

	type ActionProgressStatus = {
		actionId: number
		progressPercent: number
		//trendLogicImplemented?: boolean
		result: SeempActionResult | null | undefined
	}

	type ReportProgressStatus = {
		numberOfProgresses: number
		numberOfFinished: number
		availableTrends?: number
		totalPercent: number
	}
	const [actionProgressStatus, setActionProgressStatus] = useState<ActionProgressStatus[]>()

	const [reportProgressStatus, setReportProgressStatus] = useState<ReportProgressStatus>()
	const progressActivated = false

	useEffect(() => {
		if (companyName === undefined) {
			return
		}

		const eventManager = new EventSourceManager()

		eventManager.bind(seempSocket2.onSeempActionResult, (data) => {
			const id = data.actionId
			const progressPercent = 100
			const result = data.result

			setActionProgressStatus((prev = []) => {
				const existingIndex = prev.findIndex((p) => p.actionId === id)
				if (existingIndex !== -1) {
					const updated = [...prev]
					updated[existingIndex] = {
						actionId: id,
						progressPercent,
						result: result,
					}
					return updated
				}
				return [
					...prev,
					{
						actionId: id,
						progressPercent,
						result: result,
					},
				]
			})
		})

		const handleActionLocked = (data: {
			reportId: number
			actionId: number
			userEmail: string
			userName: string
		}) => {
			setLockedActionList((prev) => [
				...prev.filter((item) => item.reportId !== data.reportId),
				{
					reportId: data.reportId,
					actions: [
						...(prev.find((item) => item.reportId === data.reportId)?.actions ?? []),
						{
							actionId: data.actionId,
							userEmail: data.userEmail,
							userName: data.userName,
						},
					],
				},
			])
		}

		const handleActionUnlocked = (data: { reportId: number; actionId: number }) => {
			setLockedActionList((prev) => [
				...prev.filter((item) => item.reportId !== data.reportId),
				{
					reportId: data.reportId,
					actions: [
						...(
							prev.find((item) => item.reportId === data.reportId)?.actions ?? []
						).filter((item) => item.actionId !== data.actionId),
					],
				},
			])
		}

		const handleInitialLockStateUpdate = (
			data: {
				reportId: number
				actions: { actionId: number; userEmail: string; userName: string }[]
			}[]
		) => {
			setLockedActionList(data)
		}

		seempSocket.onActionsLockedStateUpdate(handleInitialLockStateUpdate)
		seempSocket.onLock(handleActionLocked)
		seempSocket.onUnlock(handleActionUnlocked)
		seempSocket.getLockedActions(companyName, plantId)

		return () => {
			seempSocket.leaveSeempReport(companyName, plantId, reportData.id, userData.email)
			seempSocket.offActionsLockedStateUpdate(handleInitialLockStateUpdate)
			seempSocket.offLock(handleActionLocked)
			seempSocket.offUnlock(handleActionUnlocked)
			eventManager.clear()
		}
	}, [companyName, reportData.id])

	useEffect(() => {
		setReportErrors((prev) =>
			prev.filter((re) => reportData.actions.map((a) => a.id).includes(re.actionId) === true)
		)
	}, [reportData.actions])

	const [isUpdateReportFormOpen, setIsUpdateReportFormOpen] = useState<boolean>(false)
	const [isDeleteReportFormOpen, setIsDeleteReportFormOpen] = useState<boolean>(false)
	const [isInfoCardOpen, setIsInfoCardOpen] = useState<boolean>(false)
	const [isAddActionFormOpen, setIsAddActionFormOpen] = useState<boolean>(false)

	const handleDeleteReport = async () => {
		if (companyName === undefined) {
			alert(t("ERROR: THE COMPANY NAME IS NOT DEFINED."))
			return
		}
		if (reportData.isPublished) {
			alert(t("ERROR: CANNOT DELETE A PUBLISHED REPORT"))
			return
		}

		if (window.confirm(t("ARE YOU SURE YOU WANT TO DELETE THIS REPORT"))) {
			const response = await requestApi2(endpoints.seempDeleteReport, {
				id: reportData.id,
			})
			if (response?.success === true) {
				alert(t("REPORT DELETED SUCCESSFULLY"))
				seempSocket.sendInvalidatePlantCacheRequest(companyName, plantId)
				seempSocket.sendPlantCacheUpdateRequest(
					companyName,
					plantId,
					"delete",
					undefined,
					reportData.id
				)
				handlePlantCacheUpdated({
					plantId,
					updateType: "delete",
					data: undefined,
					reportId: reportData.id,
				})
			} else {
				alert(t("FAILED TO DELETE REPORT"))
			}
		}
	}

	const handlePublishReport = async () => {
		if (companyName === undefined) {
			alert(t("ERROR: THE COMPANY NAME IS NOT DEFINED."))
			return
		}

		if (window.confirm(t("ARE YOU SURE YOU WANT TO PUBLISH THIS REPORT"))) {
			const reportUpdated = await requestApi2(endpoints.seempUpdateReport, {
				reportId: reportData.id,
				name: reportData.name,
				year: reportData.year,
				isPublished: true,
				socketId: portalSocket.getSocketId(),
			})

			if (reportUpdated === null) {
				alert(t("FAILED TO PUBLISH REPORT"))
				return
			}

			seempSocket.sendInvalidatePlantCacheRequest(companyName, plantId)
			seempSocket.sendPlantCacheUpdateRequest(
				companyName,
				plantId,
				"update",
				{
					...reportData,
					isPublished: true,
				},
				reportData.id
			)
			handlePlantCacheUpdated({
				plantId,
				updateType: "update",
				data: {
					...reportData,
					isPublished: true,
				},
				reportId: reportData.id,
			})
		}
	}

	const handleAmountErrors = (actionId: number, nErrors: number) => {
		setReportErrors((prev) => {
			if (prev.find((item) => item.actionId === actionId) === undefined) {
				return [...prev, { actionId, nErrors }]
			}
			return prev.map((item) => (item.actionId === actionId ? { ...item, nErrors } : item))
		})
	}

	const opModeIdsNameMap: { opModeIs: number[]; opModeLabel: string }[] = []

	const actionIdGroupMap: { type: string[]; label: string }[] = []
	for (const group of actionGroups) {
		const type = group.actionsDefinitions.map((def) => def.type)

		actionIdGroupMap.push({
			type: type,
			label: group.label,
		})
	}

	const groupedActions: Record<string, SeempAction[]> = {}

	for (const action of reportData.actions) {
		const key =
			actionIdGroupMap.find((group) => group.type.includes(action.actionType))?.label ??
			"unknown"
		if (groupedActions[key] === undefined) {
			groupedActions[key] = []
		}
		groupedActions[key].push(action)
	}

	const [isGrouped, setIsGrouped] = useState<boolean>(true)
	const [sortOrder, setSortOrder] = useState<
		"alphAsc" | "alphDesc" | "warningsMore" | "warningsLess"
	>("alphAsc")

	// Controlled accordion open state to ensure expansion after async data load
	const groupLabels = useMemo(() => Object.keys(groupedActions), [groupedActions])
	const actionIdStrings = useMemo(
		() => reportData.actions.map((action) => action.id.toString()),
		[reportData.actions]
	)
	const [openItems, setOpenItems] = useState<string[]>([])
	const hasInitializedAccordion = useRef(false)

	useEffect(() => {
		// Initialize only once when data first becomes available
		if (hasInitializedAccordion.current === false) {
			if (isGrouped === true && groupLabels.length > 0) {
				setOpenItems(groupLabels)
				hasInitializedAccordion.current = true
				return
			}
			if (isGrouped === false && actionIdStrings.length > 0) {
				setOpenItems(actionIdStrings)
				hasInitializedAccordion.current = true
			}
		}
	}, [isGrouped, groupLabels, actionIdStrings])

	useEffect(() => {
		// If user switches grouping, expand all relevant items once (do not re-run for same mode)
		if (hasInitializedAccordion.current === true) {
			if (isGrouped === true) {
				setOpenItems(groupLabels)
			} else {
				setOpenItems(actionIdStrings)
			}
		}
	}, [isGrouped])

	useEffect(() => {
		const tempProgress: {
			actionId: number
			resultIsComputed: boolean
			percent: number
			complete: boolean
		}[] = []

		for (const action of reportData.actions) {
			const complete = action.resultIsComputed
			const percent =
				complete === true
					? 100
					: (actionProgressStatus?.find((a) => a.actionId === action.id)
							?.progressPercent ?? 0)

			if (action.actionType !== "custom") {
				tempProgress.push({
					actionId: action.id,
					resultIsComputed: action.resultIsComputed,
					percent,
					complete: complete || percent >= 100,
				})
			}
		}

		const numberOfProgresses = tempProgress.length
		const numberOfFinished = tempProgress.filter((p) => p.complete === true).length
		const totalPercent =
			tempProgress.reduce((acc, p) => acc + p.percent, 0) / tempProgress.length

		setReportProgressStatus({
			numberOfProgresses: numberOfProgresses,
			numberOfFinished: numberOfFinished,
			totalPercent: totalPercent,
		})
	}, [reportData.actions.length, actionProgressStatus])

	return companyName !== undefined && usersList !== null && usersList !== undefined ? (
		<>
			<div
				className={`shadow-card sticky top-13 z-10 -mt-5 mb-5 -ml-5 flex h-12.25 w-[calc(100%+40px)] flex-row items-center ${reportData.isPublished === true ? "justify-end" : "justify-between"} gap-4 border-b-1 bg-white px-5`}
			>
				{reportData.isPublished === true ? null : (
					<Button
						variant={"filled-secondary"}
						disabled={
							(lockedActionList.find((r) => r.reportId === reportData.id)?.actions
								.length ?? 0) > 0 || totalErrors > 0
						}
						onClick={handlePublishReport}
					>
						<Button.Label>{t("PUBLISH")}</Button.Label>
					</Button>
				)}

				<div className={`flex gap-2`}>
					<DropdownMenu.Root>
						<DropdownMenu.Trigger asChild>
							<IconButton
								variant={"text-dark"}
								aria-label="sort actions"
							>
								<IconButton.Icon>{MdSort}</IconButton.Icon>
							</IconButton>
						</DropdownMenu.Trigger>
						<DropdownMenu.Portal>
							<DropdownMenu.Content
								align="end"
								alignOffset={0}
								className="text-dipai-secondary-901 border-dipai-secondary-901/20 data-[side=bottom]:animate-slideUpAndFade data-[side=left]:animate-slideRightAndFade data-[side=right]:animate-slideLeftAndFade data-[side=top]:animate-slideDownAndFade shadow-card z-1000 grid min-w-55 grid-cols-1 gap-0.5 rounded-sm border-1 bg-white p-2 will-change-[opacity,transform]"
								sideOffset={4}
							>
								<DropdownMenu.Label className="text-dipai-secondary-901 text-tiny pl-1 leading-6 font-semibold">
									{t("SORT BY")}
								</DropdownMenu.Label>
								<DropdownCheckboxItem
									checked={sortOrder === "alphAsc"}
									onCheckedChange={() => setSortOrder("alphAsc")}
									label={`${t("ALPHABETICAL")} ${t("ASC.")} (A→Z)`}
									showCheckBox={false}
								/>
								<DropdownCheckboxItem
									checked={sortOrder === "alphDesc"}
									onCheckedChange={() => setSortOrder("alphDesc")}
									label={`${t("ALPHABETICAL")} ${t("DSC.")} (Z→A)`}
									showCheckBox={false}
								/>
								<DropdownCheckboxItem
									checked={sortOrder === "warningsMore"}
									onCheckedChange={() => setSortOrder("warningsMore")}
									label={`${t("WARNINGS")} (${t("HIGH")}→${t("LOW")})`}
									showCheckBox={false}
								/>
								<DropdownCheckboxItem
									checked={sortOrder === "warningsLess"}
									onCheckedChange={() => setSortOrder("warningsLess")}
									label={`${t("WARNINGS")} (${t("LOW")}→${t("HIGH")})`}
									showCheckBox={false}
								/>
								<DropdownMenu.Label className="text-dipai-secondary-901 text-tiny pt-1 pl-1 leading-6 font-semibold">
									{t("GROUP BY")}
								</DropdownMenu.Label>
								<DropdownCheckboxItem
									checked={isGrouped}
									onCheckedChange={() => setIsGrouped(true)}
									label={`${t("OPERATION MODE")}`}
									showCheckBox={false}
								/>
								<DropdownCheckboxItem
									checked={!isGrouped}
									onCheckedChange={() => setIsGrouped(false)}
									label={`${t("UNGROUPED")}`}
									showCheckBox={false}
								/>
							</DropdownMenu.Content>
						</DropdownMenu.Portal>
					</DropdownMenu.Root>
					<IconButton
						variant={"text-dark"}
						disabled={
							reportData.isPublished === true ||
							(reportData.createdBy !== userData.email &&
								reportData.responsibleUsers.includes(userData.email) === false) ||
							(
								lockedActionList.find((r) => r.reportId === reportData.id)
									?.actions ?? []
							).length > 0
						}
						onClick={() => setIsDeleteReportFormOpen(true)}
					>
						<IconButton.Icon>{MdDelete}</IconButton.Icon>
					</IconButton>
					<IconButton
						variant={"text-dark"}
						// prettier-ignore
						disabled={
							reportData.isPublished === true ||
							(reportData.createdBy !== userData.email &&
							reportData.responsibleUsers.includes(userData.email) === false)
						}
						onClick={() => setIsUpdateReportFormOpen(true)}
					>
						<IconButton.Icon>{MdSettings}</IconButton.Icon>
					</IconButton>

					<IconButton
						variant="text-dark"
						onClick={() => setIsInfoCardOpen(true)}
					>
						<IconButton.Icon>{MdInfoOutline}</IconButton.Icon>
					</IconButton>
				</div>
			</div>

			<div className={`flex h-full w-full flex-col gap-4 rounded-sm`}>
				<div
					className={`shadow-card flex w-full max-w-full flex-col rounded-sm bg-white p-3`}
				>
					{
						<p>
							{t(`CREATED BY`)}:{" "}
							{usersList.find((u) => u.email === reportData.createdBy)?.name ??
								reportData.createdBy}
						</p>
					}
					{
						<p>
							{t(`TARGET YEAR`)}: {reportData.year}
						</p>
					}
				</div>

				<div className={`flex h-full w-full flex-row gap-4`}>
					<SeempNewActionCard
						reportId={reportData.id}
						actionsTypeList={reportData.actions.map((a) => a.actionType)}
						actionGroups={actionGroups}
					/>
					{progressActivated &&
					reportProgressStatus !== undefined &&
					reportProgressStatus.numberOfProgresses !==
						reportProgressStatus.numberOfFinished ? (
						<div
							className={`shadow-card flex w-1/4 flex-col items-center justify-center gap-2 rounded-sm bg-white p-3`}
						>
							{/* <p className={"text-dipai-secondary-901 text-menu h-8 font-semibold"}>
								{t("Loading trends ...")}
							</p> */}

							<div className={``}>
								<LoadingCircle size="sm" />
							</div>

							{`Loading trends: ${reportProgressStatus.numberOfFinished} /
							${reportProgressStatus.numberOfProgresses} (${Math.round(reportProgressStatus.totalPercent)}%)`}

							{/* {`Ongoing Progresses: ${progressStatus.numberOfProgresses - progressStatus.numberOfFinished}`}
							{`ReportProgress: ${reportProgresses}`} */}
						</div>
					) : null}
				</div>

				{/* <Button
					variant={"text-dark"}
					onClick={() => setIsAddActionFormOpen(true)}
				>
					<Button.Icon>{MdAdd}</Button.Icon>
					<Button.Label>{t("ADD ACTIONS")}</Button.Label>
				</Button> */}
				<Accordion.Root
					className="flex w-full flex-col gap-4"
					type="multiple"
					value={openItems}
					onValueChange={setOpenItems}
				>
					{isGrouped === true
						? Object.keys(groupedActions)
								.sort((a, b) => {
									if (
										sortOrder === "warningsMore" ||
										sortOrder === "warningsLess"
									) {
										const actionsA = groupedActions[a]
										const actionsB = groupedActions[b]
										if (actionsA === undefined || actionsB === undefined) {
											return 0
										}
										const nWarningsA = actionsA.reduce(
											(acc, action) =>
												acc +
												(reportErrors.find((e) => e.actionId === action.id)
													?.nErrors ?? 0),
											0
										)
										const nWarningsB = actionsB.reduce(
											(acc, action) =>
												acc +
												(reportErrors.find((e) => e.actionId === action.id)
													?.nErrors ?? 0),
											0
										)
										if (nWarningsA > nWarningsB)
											return -1 * (sortOrder === "warningsLess" ? -1 : 1)
										if (nWarningsA < nWarningsB)
											return 1 * (sortOrder === "warningsLess" ? -1 : 1)
										return a.localeCompare(b)
									}
									return sortOrder === "alphAsc"
										? a.localeCompare(b)
										: b.localeCompare(a)
								})
								.map((modeLabel) => {
									const actions = groupedActions[modeLabel]
									if (actions === undefined) {
										return null
									}
									return (
										<SeempActionGroupComponent
											key={`actionGroup_${modeLabel}`}
											modeLabel={modeLabel}
											nWarnings={actions.reduce(
												(acc, action) =>
													acc +
													(reportErrors.find(
														(e) => e.actionId === action.id
													)?.nErrors ?? 0),
												0
											)}
											showDeviation={false}
										>
											{actions
												.sort((a, b) => {
													return a.actionType.localeCompare(b.actionType)
												})
												.map((action) => (
													<SeempActionCard
														key={`actionCard_${action.id}`}
														lockedActionList={lockedActionList}
														plantId={plantId}
														reportId={reportData.id}
														actionData={action}
														handleAmountErrors={handleAmountErrors}
														usersList={usersList}
														displayModeLabel={false}
														actionGroups={actionGroups}
													/>
												))}
										</SeempActionGroupComponent>
									)
								})
						: reportData.actions
								.sort((a, b) => {
									const aModeName = opModeIdsNameMap.find(
										(m) => `${m.opModeIs.sort()}` === `${a.opModeIds.sort()}`
									)?.opModeLabel
									const bModeName = opModeIdsNameMap.find(
										(m) => `${m.opModeIs.sort()}` === `${b.opModeIds.sort()}`
									)?.opModeLabel
									if (
										sortOrder === "warningsMore" ||
										sortOrder === "warningsLess"
									) {
										const nWarningsA =
											reportErrors.find((e) => e.actionId === a.id)
												?.nErrors ?? 0
										const nWarningsB =
											reportErrors.find((e) => e.actionId === b.id)
												?.nErrors ?? 0
										if (nWarningsA > nWarningsB)
											return -1 * (sortOrder === "warningsLess" ? -1 : 1)
										if (nWarningsA < nWarningsB)
											return 1 * (sortOrder === "warningsLess" ? -1 : 1)
										if (aModeName === undefined || bModeName === undefined) {
											return 0
										}
										return `${aModeName}: ${a.actionType}`.localeCompare(
											`${bModeName}: ${b.actionType}`
										)
									}
									if (aModeName === undefined || bModeName === undefined) {
										return 0
									}
									return sortOrder === "alphAsc"
										? `${aModeName}: ${a.actionType}`.localeCompare(
												`${bModeName}: ${b.actionType}`
											)
										: `${bModeName}: ${b.actionType}`.localeCompare(
												`${aModeName}: ${a.actionType}`
											)
								})
								.map((action) => (
									<SeempActionCard
										key={`actionCard_${action.id}`}
										lockedActionList={lockedActionList}
										plantId={plantId}
										reportId={reportData.id}
										actionData={action}
										handleAmountErrors={handleAmountErrors}
										usersList={usersList}
										actionGroups={actionGroups}
									/>
								))}
				</Accordion.Root>
			</div>
			<ModalNL
				// initialFocus={multiSelectRef}
				isOpen={isUpdateReportFormOpen}
				setIsOpen={setIsUpdateReportFormOpen}
				opacity={0.75}
			>
				<SeempUpdateReportBody
					companyName={companyName}
					reportData={reportData}
					plantId={plantId}
					setModalOpenState={setIsUpdateReportFormOpen}
				/>
			</ModalNL>

			<ModalNL
				isOpen={isDeleteReportFormOpen}
				setIsOpen={setIsDeleteReportFormOpen}
				opacity={0.75}
			>
				<SeempDeleteReportBody
					companyName={companyName}
					reportData={reportData}
					plantId={plantId}
					setModalOpenState={setIsDeleteReportFormOpen}
				/>
			</ModalNL>
			<ModalNL
				isOpen={isInfoCardOpen}
				setIsOpen={setIsInfoCardOpen}
				opacity={0.75}
			>
				<SeempInfoCard setModalOpenState={setIsInfoCardOpen} />
			</ModalNL>
			{/* <ModalNL
				// initialFocus={multiSelectRef}
				isOpen={isAddActionFormOpen}
				setIsOpen={setIsAddActionFormOpen}
				opacity={0.75}
			>
				<SeempNewActionCard
					reportId={reportData.id}
					actionsTypeList={reportData.actions.map((a) => a.actionType)}
					actionGroups={actionGroups}
				/>
			</ModalNL> */}
		</>
	) : (
		"loading"
	)
}

export const DropdownCheckboxItem = ({
	checked,
	onCheckedChange,
	label,
	userAvatar,
	showCheckBox = true,
	onSelect,
}: {
	checked: boolean
	onCheckedChange: () => void
	label: string
	showCheckBox?: boolean
	userAvatar?: { avatarColor: string; userName: string }
	onSelect?: (e: Event) => void
}) => {
	return (
		<DropdownMenu.CheckboxItem
			className="data-[highlighted]:bg-dipai-secondary-100 data-[disabled]:text-dipai-secondary-400 data-[highlighted]:text-dipai-secondary-800 text-tiny relative flex h-6 items-center rounded-sm pr-1 pl-6 leading-none outline-none select-none data-[disabled]:pointer-events-none"
			checked={checked}
			onCheckedChange={onCheckedChange}
			onSelect={onSelect}
		>
			{checked === true ? (
				<DropdownMenu.ItemIndicator
					className={`${showCheckBox === true ? "border-1 bg-white" : "border-0"} border-dipai-secondary-901/20 absolute left-1 inline-flex size-4 items-center justify-center rounded-sm`}
				>
					<CheckIcon />
				</DropdownMenu.ItemIndicator>
			) : showCheckBox === true ? (
				<div
					className={`border-dipai-secondary-901/20 absolute left-1 size-4 rounded-sm border-1 bg-white`}
				/>
			) : null}
			{userAvatar !== undefined ? (
				<div
					style={{ backgroundColor: userAvatar.avatarColor }}
					className={`relative mr-1 inline-block size-4.5 rounded-full border-0 text-center align-middle`}
				>
					<p className="text-tiny absolute top-1/2 left-1/2 -translate-1/2 leading-none font-bold text-white/90 uppercase">
						{userAvatar.userName.charAt(0).toUpperCase()}
					</p>
				</div>
			) : null}
			{label}
		</DropdownMenu.CheckboxItem>
	)
}

function SeempAddActionForm({
	companyName,
	plantId,
	reportData,
	setModalOpenState,
}: {
	companyName: string
	plantId: number
	reportData: SeempReport
	setModalOpenState: (isOpen: boolean) => void
}) {
	const queryClient = useQueryClient()
	const handlePlantCacheUpdated = getHandlePlantCacheUpdated(queryClient)
	const [newName, setNewName] = useState<string>(reportData.name)
	const [newYear, setNewYear] = useState<number>(reportData.year)

	const handleUpdateReport = async (newName: string, newYear: number) => {
		if (newName === reportData.name && newYear === reportData.year) {
			return
		}

		const reportUpdated = await requestApi2(endpoints.seempUpdateReport, {
			reportId: reportData.id,
			name: newName,
			year: newYear,
			isPublished: reportData.isPublished,
			socketId: portalSocket.getSocketId(),
		})
		if (reportUpdated === null) {
			alert(t("FAILED TO UPDATE REPORT"))
			setNewName("Seemp Report")
			setNewYear(new Date().getFullYear() + 1)
			return
		}

		seempSocket.sendInvalidatePlantCacheRequest(companyName, plantId)
		seempSocket.sendPlantCacheUpdateRequest(
			companyName,
			plantId,
			"update",
			{ ...reportData, name: newName, year: newYear, isPublished: reportData.isPublished },
			reportData.id
		)
		handlePlantCacheUpdated({
			plantId,
			updateType: "update",
			data: {
				...reportData,
				name: newName,
				year: newYear,
				isPublished: reportData.isPublished,
			},
			reportId: reportData.id,
		})
		setNewName("Seemp Report")
		setNewYear(new Date().getFullYear() + 1)
		setModalOpenState(false)
	}

	return (
		<div
			className={`shadow-card fixed inset-1/2 flex h-fit w-80 -translate-1/2 flex-col rounded-sm border-0 bg-white p-3`}
		>
			<p className={"text-dipai-secondary-901 text-menu h-8 font-semibold"}>
				{t("UPDATE SEEMP REPORT")}
			</p>
			<div className={`flex w-full flex-col justify-between gap-3`}>
				<div className={`flex flex-row gap-3`}>
					<div className={`flex w-[calc(100%-5rem)] flex-col gap-1.5`}>
						<label
							htmlFor="report-name"
							className={`text-tiny h-4`}
						>
							{t("REPORT NAME")}
						</label>
					</div>
					<div className={`flex w-20 flex-col gap-1.5`}>
						<label
							htmlFor="report-year"
							className={`text-tiny h-4`}
						>
							{t("TARGET YEAR")}
						</label>
						<input
							id="report-year"
							type="number"
							min={new Date().getFullYear()}
							max={new Date().getFullYear() + 3}
							onChange={(e) => {
								setNewYear(Number(e.target.value))
							}}
							value={newYear}
							className={`border-dipai-secondary-901/20 text-tiny hover:border-dipai-secondary-500 focus:ring-dipai-secondary-500 h-8 rounded-sm border-1 p-2`}
						/>
					</div>
				</div>
				<div className={`flex flex-row justify-end gap-3`}>
					<Button
						variant={"outline-dark"}
						onClick={() => setModalOpenState(false)}
					>
						<Button.Label>{t("CANCEL")}</Button.Label>
					</Button>
					<Button
						variant={"filled-secondary"}
						disabled={reportData.name === newName && newYear === reportData.year}
						onClick={() => handleUpdateReport(newName, newYear)}
					>
						<Button.Label>{t("UPDATE")}</Button.Label>
					</Button>
				</div>
			</div>
		</div>
	)
}
